import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OutputRefSubscription, viewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BookingFlowComponent } from './components/booking-flow/booking-flow.component';
import { TitleBarComponent, TitleBarViewModel } from './components/title-bar/title-bar.component';
import { ConfigService } from './services/config/config.service';

declare const gtag: Function;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false,
})
export class AppComponent implements OnDestroy, AfterViewInit {
    public isLoading = false;
    public showAccountContainer = false;
    public titleBarViewModel: TitleBarViewModel | undefined = { title: 'Daysmart Booking' };
    private _bookingFlowChangedSubscription: OutputRefSubscription | undefined;
    private _routerOutletComponent: any;
    public titleBarComponent = viewChild(TitleBarComponent, { read: ElementRef });
    private _mutationObserver: MutationObserver | undefined;

    constructor(
        private router: Router,
        private readonly configService: ConfigService
    ) {
        this.addGaScript();
        this.setUpAnalytics();
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any) {
        this.updateBookingFlowTitleBarHeight();
    }

    ngAfterViewInit() {
        this.observerTitleBar();
    }

    public onRouterOutletActivate(component: any): void {
        this._routerOutletComponent = component;
        if (component instanceof BookingFlowComponent) {
            this._bookingFlowChangedSubscription = component.stepChanged?.subscribe((stepComponent) => {
                this.titleBarViewModel = {
                    title: stepComponent?.titleBarTitle ?? 'Booking',
                    subtitle: stepComponent?.titleBarSubtitle,
                    showBackButton: stepComponent?.showTitleBarBackButton,
                    showAccountSection: stepComponent?.showTitleBarAccountSection,
                };
                this.snapToTop();
                this.updateBookingFlowTitleBarHeight();
            });
            if (!this._mutationObserver) {
                this.observerTitleBar();
            }
            this.updateBookingFlowTitleBarHeight();
        } else {
            this._mutationObserver?.disconnect();
            this._mutationObserver = undefined;
            this.titleBarViewModel = undefined;
        }
    }

    private observerTitleBar(): void {
        this._mutationObserver = new MutationObserver(() => {
            this.updateBookingFlowTitleBarHeight();
        });
        const titleBarComponent = this.titleBarComponent();
        if (titleBarComponent?.nativeElement) {
            this._mutationObserver.observe(titleBarComponent.nativeElement, {
                attributes: true,
                childList: true,
                subtree: true,
                characterData: true,
            });
        }
    }

    private snapToTop(): void {
        window.scrollTo({ top: 0, behavior: 'instant' });
    }

    private updateBookingFlowTitleBarHeight(): void {
        if (!this._routerOutletComponent || !(this._routerOutletComponent instanceof BookingFlowComponent)) {
            return;
        }
        const component = this._routerOutletComponent as BookingFlowComponent;
        requestAnimationFrame(() => {
            const titleBarContainer = this.titleBarComponent()?.nativeElement;
            if (titleBarContainer) {
                const titleBarContainerHeight = titleBarContainer.getBoundingClientRect().height;
                component.titleBarHeight.set(`${titleBarContainerHeight}px`);
            }
        });
    }

    private addGaScript() {
        const gtagScript: HTMLScriptElement = document.createElement('script');
        gtagScript.async = true;
        gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=' + this.configService.config.gaTrackingID;
        document.head.prepend(gtagScript);
        /** Disable automatic page view hit to fix duplicate page view count  **/
        gtag('config', this.configService.config.gaTrackingID, { send_page_view: false });
    }

    private setUpAnalytics() {
        /* istanbul ignore next */
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: any) => {
            gtag('event', 'page_view', {
                page_path: event.urlAfterRedirects,
            });
        });
    }

    public onGoBack(): void {
        if (this._routerOutletComponent && typeof this._routerOutletComponent.return === 'function') {
            this._routerOutletComponent.return();
        }
    }

    ngOnDestroy() {
        this._bookingFlowChangedSubscription?.unsubscribe();
        this._bookingFlowChangedSubscription = undefined;
        this._mutationObserver?.disconnect();
        this._mutationObserver = undefined;
    }
}
